import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { defer, EMPTY, from, merge, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { sItemKeyToInt, StockItem, StockItemUpdateObj } from '../../../shared-utilities/models-old/datastructures';
import { StockTakeFirebaseService } from '../../stock-take/services/stock-take-firebase.service';
import { StockTakeItemStatus } from '../../stock-take/models';
import { IGlobalError } from '../../../shared/shared-components/components/global-error-display/models/global-error';
import { SharedModalBasicComponent } from '../../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import { SharedModalOrderRecipientsComponent } from '../../../shared/shared-components/components/shared-modal-order-recipients/shared-modal-order-recipients.component';
import { ICloudEmailer } from '../../../shared/shared-models/cloud/cloud-emailer';
import { IFirebaseQuery } from '../../../shared/shared-models/firebase/firebase-queries';
import { ISharedModalBasic, ISharedModalBasicResponse } from '../../../shared/shared-models/modals/shared-modal-basic';
import { IconColourEnum } from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import { IconsEnum } from '../../../shared-modules/shared-icons/models/icons.enum';
import { IPaginationData } from '../../../shared/shared-models/pagination/pagination-data';
import { ISupplier } from '../../../shared/shared-models/stock/suppliers';
import { IStore } from '../../../shared/shared-models/store/store';
import {
  IFacetCounts,
  TypeSenseSearchResponse,
} from '../../../shared/shared-models/type-sense/type-sense-types';
import {
  logConsoleError,
  logConsoleInfo,
  logConsoleSuccess,
} from '../../../shared/shared-module-ui/utils/logger/logger';
import { environment } from '../../../../environments/environment';
import {
  path_admin_cloud_emailer_email_orders,
  path_admin_functions_orders_instructions_delete_order,
  path_operational_stores_data_auto_ordering_auto_orders_GET,
  path_operational_stores_data_auto_ordering_auto_orders_order_id,
  path_operational_stores_data_storeId_data_auto_ordering_supplier_emails,
  path_operational_stores_data_storeId_data_auto_ordering_supplier_emails_suppId,
  path_stores_storeId_settings_ao_order_expiration,
  path_users_userId_settings_stores_storeId_orders_settings,
} from '../../../shared/shared-services/database-paths';
import {
  PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID,
  PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID,
} from '../../../shared/shared-services/database-paths.operational';
import { createSharedPaths } from '../../../shared/shared-services/database-paths.shared';
import { CollectionAdminCloudService } from '../../../shared/shared-services/firebase/collection-admin-cloud.service';
import { CollectionAdminFunctionsService } from '../../../shared/shared-services/firebase/collection-admin-functions.service';
import { CollectionOperationalAutoOrderingService } from '../../../shared/shared-services/firebase/collection-operational-auto-ordering.service';
import { CollectionOperationalSingularDocumentsService } from '../../../shared/shared-services/firebase/collection-operational-singular-documents.service';
import { CollectionSharedStockService } from '../../../shared/shared-services/firebase/collection-shared-stock.service';
import { CollectionSharedSuppliersService } from '../../../shared/shared-services/firebase/collection-shared-suppliers.service';
import { CollectionUsersSettingsService } from '../../../shared/shared-services/firebase/collection-users-settings.service';
import {
  IModalDismissResult,
  IModalRef,
  ModalControllerService,
} from '../../../shared/shared-services/overlay/modal-controller.service';
import { TypeSenseService } from '../../../shared/shared-services/type-sense/type-sense.service';
import { encodeStrict } from '../../../shared/shared-utils/firestore/firestore.utils';
import {
  filterValidEmails,
  findInvalidEmails,
} from '../../../shared/shared-utils/form/email.validator';
import { calculatePercentile } from '../../../shared/shared-utils/number/number.utils';
import {
  createFacetCountsMap,
  mapTypeSenseObjectToGeneric,
} from '../../../shared/shared-utils/typesense-utils/typesense.utils';
import { LocalStorageKeysEnum } from '../../core/models';
import { LocalStorageService } from '../../core/service';
import * as SharedActions from '../../core/store-shared/shared.actions';
import * as SharedSelectors from '../../core/store-shared/shared.selectors';
import * as NotificationActions from '../../notifications/store/notification.actions';
import {
  IAutoOrderExpirationSettings,
  IAutoOrderOrderingSettings,
  IDisableStockItemRule,
  TLineColour,
} from '../../settings-new/models';
import * as StoreSettingsActions from '../../settings/store/settings.actions';
import { selectOpenOrderSettings } from '../../settings/store/settings.selectors';
import * as UserAccessSelectors from '../../user-access/user-store/user.selectors';
import * as UserStoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import { selectSelectedStore } from '../../user-access/user-stores-store/user-store.selectors';
import {
  AUTO_ORDER_OPEN_ORDER_FLAG_ITEMS,
  AutoOrderQuantityWriteResultEnum,
  DEFAULT_AUTO_ORDER_PROGRESS_STEP,
  DEFAULT_OPEN_AUTO_GENERATED_ORDER,
  IAutoOrderFirestoreOrderItem,
  IAutoOrderOrder,
  IAutoOrderOrderItem,
  IAutoOrderOrderProgressStep,
  IAutoOrderOrderStockItem,
  IAutoOrderQuantityWriteLog,
  IAutoOrderSupplierEmails,
  IOpenAutoGeneratedOrder,
} from '../models';
import { AutoOrder } from '../models/old-models/ao-datastructures';
import { AutoOrderService, AUTO_ORDER_RETRY_NOT_VALID } from '../service/auto-order.service';
import { shouldClearPendingRetryAutoOrder } from '../utils/auto-order-failure-recovery.utils';
import {
  buildAutoOrderQuantityWriteLog,
  getAutoOrderQuantityWriteErrorMessage,
} from '../utils/auto-order-quantity-write-log.utils';
import {
  countAutoOrderCompletedSteps,
  countAutoOrderTotalSteps,
  determineAutoOrderMissingStockCodes,
  determineMissingAutoOrderItemCodes,
  groupOrderItemsByCode,
} from '../utils/open-auto-order.utils';
import {
  localStorageKeyOrderOnHand,
  localStorageKeyOrderPrice,
  localStorageKeyOrderQuantity,
} from '../utils/auto-ordering-order-grid.utils';
import { areQuantityDraftsEqual } from '../utils/auto-order-prepared-items.utils';
import { buildDisableStockItemUpdate } from '../utils/auto-order-disable-item.utils';
import {
  presentSharedOverlayLoadingModal,
} from '../../../shared/shared-components/components/shared-overlay-loading-modal/shared-overlay-loading-modal.component';
import { selectItemEnablingSettings } from '../../settings/store/settings.selectors';
import * as AutoOrderActions from './auto-ordering.actions';
import { clearAllAutoOrderSupplierEmails } from './auto-ordering.actions';
import * as AutoOrderSelectors from './auto-ordering.selectors';
import { FirebaseService } from 'src/app/shared-utilities/services-old/firebase.service';

/**
 * Adapter function to convert IAutoOrderOrder to legacy AutoOrder type.
 * This ensures type safety when calling legacy FirebaseService methods.
 *
 * @param order - The modern IAutoOrderOrder object
 * @returns AutoOrder - The legacy AutoOrder format
 */
function adaptToLegacyAutoOrder(order: IAutoOrderOrder): AutoOrder {
  // Convert orderItems array to Record<string, IAutoOrderOrderItem>
  const orderItemsRecord: Record<string, IAutoOrderOrderItem> = {};
  if (Array.isArray(order.orderItems)) {
    order.orderItems.forEach((item: IAutoOrderFirestoreOrderItem) => {
      if (item.code) {
        orderItemsRecord[item.code] = item;
      }
    });
  }

  // Map status to legacy format (legacy doesn't support 'CREATED' or 'DELETING')
  let legacyStatus: 'EMPTY' | 'READY' | 'PREPARED' | 'SUBMITTED' | 'FAILED' | 'SUCCESS';
  if (order.status === 'CREATED' || order.status === 'DELETING') {
    legacyStatus = 'READY'; // Map unsupported statuses to READY
  } else {
    legacyStatus = order.status;
  }

  return {
    generated:
      order.generated instanceof Date
        ? order.generated
        : new Date(order.generated as unknown as string),
    orderId: order.orderId,
    orderItems: orderItemsRecord,
    orderSettings: order.orderSettings,
    owned: order.owned,
    scheduled: order.scheduled,
    status: legacyStatus,
    supplierId: order.supplierId,
    userId: order.userId,
    // Legacy-only fields (not present in IAutoOrderOrder)
    archived: false as NonNullable<false>, // Legacy type requires false
    linkedToSupplierId: order.linkedToSupplierId,
    linkedToSupplierName: order.linkedToSupplierName,
    onHold: Array.isArray(order.onHold) ? order.onHold.join(',') : order.onHold,
    orderTotal: order.orderTotal,
    preparedInfo: order.preparedInfo,
    // processed doesn't exist on IAutoOrderOrder
    processed: undefined,
    recipients: order.recipients,
    supplierName: order.supplierName,
  };
}

@Injectable()
export class AutoOrderingEffects {
  //#region [AUTO ORDER][Open Step 01] Open Auto Order

  //#region [AUTO ORDER][Open Step 01] Open Auto Order
  openAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.openAutoOrder),
      withLatestFrom(
        this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(AutoOrderSelectors.selectAutoGeneratedOrdersForSelectedOrder)),
        this.store.pipe(select(SharedSelectors.selectSuppliersByUserSelectedStore))
      ),
      switchMap(([action, isLoggedIn, storeDetails, orderDetails, suppliers]) => {
        if (!isLoggedIn && !orderDetails?.orderId) return EMPTY;
        const { orderId } = action;

        if (!orderDetails || orderId.trim() !== orderDetails.orderId?.trim()) {
          logConsoleError('[AUTO ORDER][Open Step 01] Open Auto Order', {
            action: action,
            message: 'Order id check fail',
            orderDetailsJson: JSON.stringify(orderDetails),
            orderDetails: orderDetails
              ? `ao-open-01-order-details`
              : 'ao-open-01-missing-order-details',
            orderId: orderId ?? 'ao-open-01-missing-order-id',
            storeDetails: storeDetails
              ? `ao-open-01-store-details`
              : 'ao-open-01-missing-store-details',
            ts: 'aoe:openAutoOrder:01:01',
          });
          this.store.dispatch(
            NotificationActions.setGlobalErrorOccurrence({
              error: {
                action: JSON.stringify(action),
                actionType: action.type,
                name: 'AutoOrderOpenOrderError',
                message: `The Selected Order ID does not match Auto Generated Order ID.`,
              },
            })
          );
          this.store.dispatch(AutoOrderActions.openAutoOrderSuccess({ orderId, success: false }));
          this.store.dispatch(
            AutoOrderActions.setOpenOrderLoadingIndicator({
              property: 'openAutoOrderOrderStarted',
              value: false,
            })
          );
          return EMPTY;
        }

        const supplierDetails = suppliers[orderDetails.supplierId];
        if (!supplierDetails) {
          logConsoleError('[AUTO ORDER][Open Step 01] Open Auto Order', {
            action: action,
            message: 'Supplier details for order not found',
            orderDetails: orderDetails
              ? JSON.stringify(orderDetails)
              : 'ao-open-01-missing-order-details',
            orderId: orderId ?? 'ao-open-01-missing-order-id',
            storeId: storeDetails?.storeId ?? 'ao-open-01-missing-store-id',
            supplierId:
              orderDetails?.supplierId ?? 'ao-open-01-missing-supplier-id-on-order-details',
            ts: 'aoe:openAutoOrder:01:02',
          });
          this.store.dispatch(
            NotificationActions.setGlobalErrorOccurrence({
              error: {
                action: JSON.stringify(action),
                actionType: action.type,
                name: 'AutoOrderOpenOrderError',
                message: `The Selected Supplier ID ${orderDetails.supplierId} does not have any information.`,
              },
            })
          );
          this.store.dispatch(
            AutoOrderActions.setOpenOrderLoadingIndicator({
              property: 'openAutoOrderOrderStarted',
              value: false,
            })
          );
          this.store.dispatch(
            AutoOrderActions.setOpenOrderLoadingIndicator({
              property: 'openAutoOrderOrderStarted',
              value: false,
            })
          );
          return EMPTY;
        }

        const steps: IAutoOrderOrderProgressStep = {
          ...DEFAULT_AUTO_ORDER_PROGRESS_STEP,
          openAutoOrderOrderStarted: true,
          getSelectedStore: true,
          setSelectedOrder: true,
          setSelectedSupplier: true,
        };

        const progress: IOpenAutoGeneratedOrder = {
          ...DEFAULT_OPEN_AUTO_GENERATED_ORDER,
          orderDetails,
          steps,
          storeDetails,
          totalSteps: countAutoOrderTotalSteps(DEFAULT_AUTO_ORDER_PROGRESS_STEP),
          success: undefined,
          supplierDetails,
          supplierStockItems: {
            additional: [],
            added: [],
            main: [],
          },
        };
        progress.progress = calculatePercentile(
          countAutoOrderCompletedSteps(progress.steps),
          progress.totalSteps
        );
        return from([AutoOrderActions.initializeOpenAutoOrder({ progress })]);
      })
    )
  );

  //#endregion

  //#region [AUTO ORDER][Open Step 02] Initialize Open Auto Order
  initializeOpenAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.initializeOpenAutoOrder),
      switchMap(({ progress }) => {
        return from([
          AutoOrderActions.getOrderItemsForOpenAutoOrder({ progress }),
          AutoOrderActions.getStockItemsForOrderRegularSupplier({
            progress,
            query: [
              {
                q: 'where',
                p: [sItemKeyToInt.regularSuppCode, '==', progress.orderDetails.supplierId],
              },
            ],
          }),
          AutoOrderActions.getStockItemsForOrderAdditionalSuppliers({
            progress,
            query: [
              {
                q: 'where',
                p: [
                  sItemKeyToInt.multipleSuppliers,
                  'array-contains',
                  progress.orderDetails.supplierId,
                ],
              },
            ],
          }),
        ]);
      })
    )
  );
  //#endregion

  //#region [AUTO ORDER][Open Step 03][01] Get Order Items for Open Auto Order
  getOrderItemsForOpenAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getOrderItemsForOpenAutoOrder),
      withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn))),
      filter(([_, isUserLoggedIn]) => isUserLoggedIn),
      switchMap(([action, isUserLoggedIn]) => {
        if (!isUserLoggedIn) return EMPTY;
        const { progress } = action;
        const store: IStore = progress.storeDetails;
        const order: IAutoOrderOrder = progress.orderDetails;

        return this.collectionOperationalAutoOrderingService
          .getAutoOrderOrderItems(store, order.orderId)
          .pipe(
            mergeMap((orderItems: IAutoOrderFirestoreOrderItem[]) => {
              const addedItems = orderItems.filter(
                (item: IAutoOrderOrderItem) => item.new === 'ADDED'
              );

              const chunkSize = 30;
              const orderItemsAdded: IAutoOrderOrderItem[][] = [];
              for (let i = 0; i < addedItems.length; i += chunkSize) {
                orderItemsAdded.push(addedItems.slice(i, i + chunkSize));
              }

              const updatedProgress: IOpenAutoGeneratedOrder = {
                ...progress,
                orderItems,
                orderItemsAdded,
                steps: {
                  ...progress.steps,
                  getOrderItems: true,
                },
                supplierStockItems: {
                  main: [],
                  added: [],
                  additional: [],
                },
              };

              const successActions: Action[] = [
                AutoOrderActions.getOrderItemsForOpenAutoOrderSuccess({
                  progress: updatedProgress,
                }),
              ];
              return from(successActions);
            }),
            catchError((error) =>
              from([
                NotificationActions.setGlobalErrorOccurrence({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    name: 'AutoOrderOpenOrderError',
                    originalError: error,
                  },
                }),
                AutoOrderActions.setOpenOrderLoadingIndicator({
                  property: 'getOrderItems',
                  value: false,
                }),
                AutoOrderActions.openAutoOrderSuccess({ orderId: order.orderId, success: false }),
              ])
            )
          );
      })
    )
  );

  //#endregion

  //#region [AUTO ORDER][Open Step 03][02] Get Order Items for Open Auto Order Success
  getOrderItemsForOpenAutoOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getOrderItemsForOpenAutoOrderSuccess),
      withLatestFrom(this.store.pipe(select(AutoOrderSelectors.selectOpenAutoGeneratedOrders))),
      mergeMap(([action, stateProgress]): Action[] => {
        const orderId: string = action?.progress?.orderDetails?.orderId;
        let progress: IOpenAutoGeneratedOrder;
        const stateOpenOrder = stateProgress?.[orderId];
        if (stateOpenOrder?.steps?.getOrderItems === true) {
          progress = {
            ...stateProgress[orderId],
            orderItems: action.progress.orderItems,
            orderItemsAdded: action.progress.orderItemsAdded,
          };
        } else {
          progress = action.progress;
        }

        const successActions: Action[] = [
          AutoOrderActions.getOrderItemsForOpenAutoOrderWithLatestProgress({ progress }),
        ];

        if (progress.orderItemsAdded.length > 0) {
          successActions.push(
            AutoOrderActions.getStockItemsForAddedItemsToOrder({
              progress,
              orderItemsAdded: progress.orderItemsAdded,
            })
          );
        } else {
          successActions.push(
            AutoOrderActions.getStockItemsForAddedItemsToOrderSuccess({
              progress,
            })
          );
        }
        return successActions;
      })
    )
  );
  //#endregion

  //#region [AUTO ORDER][Open Step 04 + 05] Get Stock Items for Suppliers
  loadStockItemsForAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AutoOrderActions.getStockItemsForOrderRegularSupplier,
        AutoOrderActions.getStockItemsForOrderAdditionalSuppliers
      ),
      withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn))),
      filter(([_, isUserLoggedIn]) => isUserLoggedIn),
      mergeMap(([action]) => {
        const { progress, query } = action;
        const store = progress.storeDetails;

        return this.store.pipe(
          select(AutoOrderSelectors.selectIsOpenOrderCRUDLoading),
          filter((isLoading: boolean): boolean => isLoading === false),
          take(1),
          switchMap(() =>
            this.collectionSharedStockService.getStockItemsCast(store, query, 'objectID').pipe(
              switchMap((stockItems: StockItem[]) => {
                const isRegularSupplier =
                  action.type === AutoOrderActions.getStockItemsForOrderRegularSupplier.type;

                const updatedProgress: IOpenAutoGeneratedOrder = {
                  ...progress,
                  steps: {
                    ...progress.steps,
                    ...(isRegularSupplier
                      ? { getStockItemsRegularSupplier: true }
                      : { getStockItemsAdditionalSupplier: true }),
                  },
                  supplierStockItems: {
                    ...{
                      additional: [],
                      added: [],
                      main: [],
                    },
                    ...progress.supplierStockItems,
                    ...(isRegularSupplier ? { main: stockItems } : { additional: stockItems }),
                  },
                };

                const successAction = isRegularSupplier
                  ? AutoOrderActions.getStockItemsForOrderRegularSupplierSuccess({
                      progress: updatedProgress,
                    })
                  : AutoOrderActions.getStockItemsForOrderAdditionalSuppliersSuccess({
                      progress: updatedProgress,
                    });

                return from([successAction]);
              }),
              catchError((error) =>
                from([
                  NotificationActions.setGlobalErrorOccurrence({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      name: 'AutoOrderOpenOrderError',
                      originalError: error,
                    },
                  }),
                  AutoOrderActions.openAutoOrderSuccess({
                    orderId: progress.orderDetails.orderId,
                    success: false,
                  }),
                  AutoOrderActions.setOpenOrderLoadingIndicator({
                    property:
                      action.type === AutoOrderActions.getStockItemsForOrderRegularSupplier.type
                        ? 'getStockItemsRegularSupplier'
                        : 'getStockItemsAdditionalSupplier',
                    value: false,
                  }),
                ])
              )
            )
          )
        );
      })
    )
  );

  //#endregion

  //#region [AUTO ORDER][Open Step 04 + 05][02] Get Stock Items for Suppliers
  loadStockItemsForAutoOrderAtLatestProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AutoOrderActions.getStockItemsForOrderRegularSupplierSuccess,
        AutoOrderActions.getStockItemsForOrderAdditionalSuppliersSuccess
      ),
      withLatestFrom(this.store.pipe(select(AutoOrderSelectors.selectOpenAutoGeneratedOrders))),
      mergeMap(([action, stateProgress]): Action[] => {
        const orderId: string = action?.progress?.orderDetails?.orderId;
        let progress: IOpenAutoGeneratedOrder;
        const stateOpenOrder = stateProgress?.[orderId];
        if (stateOpenOrder?.steps?.getOrderItems === true) {
          progress = {
            ...stateProgress[orderId],
            supplierStockItems: {
              ...stateProgress[orderId].supplierStockItems,
              added: [
                ...stateProgress[orderId].supplierStockItems.added,
                ...action.progress.supplierStockItems.added,
              ],
              additional: [
                ...stateProgress[orderId].supplierStockItems.additional,
                ...action.progress.supplierStockItems.additional,
              ],
              main: [
                ...stateProgress[orderId].supplierStockItems.main,
                ...action.progress.supplierStockItems.main,
              ],
            },
          };
        } else {
          progress = action.progress;
        }

        const isRegularSupplier =
          action.type === AutoOrderActions.getStockItemsForOrderRegularSupplierSuccess.type;
        const successAction = isRegularSupplier
          ? AutoOrderActions.getStockItemsForOrderRegularSupplierWithLatestProgress({ progress })
          : AutoOrderActions.getStockItemsForOrderAdditionalSuppliersWithLatestProgress({
              progress,
            });

        return [successAction];
      })
    )
  );
  //#endregion

  //#region [AUTO ORDER][Open Step 06][01] Get Stock Items for Added Items to Order
  getStockItemsForAddedItemsToOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getStockItemsForAddedItemsToOrder),
      withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn))),
      filter(([_, isUserLoggedIn]) => isUserLoggedIn),
      mergeMap(([action]) => {
        const { progress, orderItemsAdded } = action;
        const store = progress.storeDetails;
        const orderItemsQuery = [...orderItemsAdded];

        return this.store.pipe(
          select(AutoOrderSelectors.selectIsOpenOrderCRUDLoading),
          filter((isLoading: boolean): boolean => isLoading === false),
          take(1),
          switchMap(() => {
            const chunk = orderItemsQuery.shift();

            if (!chunk) {
              return of(
                AutoOrderActions.getStockItemsForAddedItemsToOrderSuccess({
                  progress,
                })
              );
            }

            const codesForQuery: string[] = chunk.map((item: IAutoOrderOrderItem) => item.code);

            const query: IFirebaseQuery[] = [
              {
                q: 'where',
                p: [sItemKeyToInt.code, 'in', codesForQuery],
              },
            ];

            return this.collectionSharedStockService
              .getStockItemsCast(store, query, 'objectID')
              .pipe(
                switchMap((stockItems: StockItem[]) => {
                  const updatedProgress: IOpenAutoGeneratedOrder = {
                    ...progress,
                    steps: {
                      ...progress.steps,
                      getOrderItemsAdded: orderItemsQuery.length < 1,
                    },
                    supplierStockItems: {
                      ...{
                        additional: [],
                        added: [],
                        main: [],
                      },
                      ...progress.supplierStockItems,
                      added: stockItems,
                    },
                  };

                  const successActions: Action[] = [
                    AutoOrderActions.getStockItemsForAddedItemsToOrderSuccess({
                      progress: updatedProgress,
                    }),
                  ];

                  if (orderItemsQuery.length > 0) {
                    successActions.push(
                      AutoOrderActions.getStockItemsForAddedItemsToOrder({
                        progress: updatedProgress,
                        orderItemsAdded: orderItemsQuery,
                      })
                    );
                  }

                  return from(successActions);
                }),
                catchError((error) =>
                  from([
                    NotificationActions.setGlobalErrorOccurrence({
                      error: {
                        ...error,
                        action: JSON.stringify(action),
                        actionType: action.type,
                        name: 'AutoOrderOpenOrderError',
                        originalError: error,
                      },
                    }),
                    AutoOrderActions.openAutoOrderSuccess({
                      orderId: progress.orderDetails.orderId,
                      success: false,
                    }),
                    AutoOrderActions.setOpenOrderLoadingIndicator({
                      property: 'getOrderItemsAdded',
                      value: false,
                    }),
                  ])
                )
              );
          })
        );
      })
    )
  );
  //#endregion

  //#region [AUTO ORDER][Open Step 06][02] Get Stock Items for Added Items to Order With Latest Progress
  getStockItemsForAddedItemsToOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getStockItemsForAddedItemsToOrderSuccess),
      withLatestFrom(this.store.pipe(select(AutoOrderSelectors.selectOpenAutoGeneratedOrders))),
      mergeMap(([action, stateProgress]): Action[] => {
        const orderId: string = action?.progress?.orderDetails?.orderId;
        let progress: IOpenAutoGeneratedOrder;
        const stateOpenOrder = stateProgress?.[orderId];
        if (stateOpenOrder?.steps?.getOrderItems === true) {
          progress = {
            ...stateProgress[orderId],
            supplierStockItems: {
              ...stateProgress[orderId].supplierStockItems,
              added: [
                ...stateProgress[orderId].supplierStockItems.added,
                ...action.progress.supplierStockItems.added,
              ],
              additional: [
                ...stateProgress[orderId].supplierStockItems.additional,
                ...action.progress.supplierStockItems.additional,
              ],
              main: [
                ...stateProgress[orderId].supplierStockItems.main,
                ...action.progress.supplierStockItems.main,
              ],
            },
          };
        } else if (action.progress?.orderDetails?.orderId) {
          progress = action.progress;
        }
        return [AutoOrderActions.getStockItemsForAddedItemsToOrderWithLatestProgress({ progress })];
      })
    )
  );
  //#endregion

  //#region [AUTO ORDER][Open Step 07] Group Stock Items by Code for Open Auto Order
  groupStockItemData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AutoOrderActions.getOrderItemsForOpenAutoOrderWithLatestProgress,
        AutoOrderActions.getStockItemsForOrderRegularSupplierWithLatestProgress,
        AutoOrderActions.getStockItemsForOrderAdditionalSuppliersWithLatestProgress,
        AutoOrderActions.getStockItemsForAddedItemsToOrderWithLatestProgress
      ),
      withLatestFrom(
        this.store.select(AutoOrderSelectors.selectOpenAutoGeneratedOrderById),
        this.store.select(UserAccessSelectors.selectIsUserLoggedIn)
      ),
      filter(([action, order, isUserLoggedIn]) => {
        return (
          !!order &&
          order.steps.getOrderItems &&
          order.steps.getStockItemsRegularSupplier &&
          order.steps.getStockItemsAdditionalSupplier &&
          order.steps.getOrderItemsAdded &&
          isUserLoggedIn
        );
      }),
      map(([_, order]) => {
        const supplierStockItems = order?.supplierStockItems ?? {
          main: [],
          additional: [],
          added: [],
        };

        const stockItemsGrouped = groupOrderItemsByCode([
          supplierStockItems.main,
          supplierStockItems.additional,
          supplierStockItems.added,
        ]);

        const updatedProgress: IOpenAutoGeneratedOrder = {
          ...order,
          steps: {
            ...order.steps,
            concatenateStockItems: true,
            deDuplicateStockItems: true,
          },
          stockItems: {
            duplicates: stockItemsGrouped.duplicates,
            other: stockItemsGrouped.nullOrUndefined,
            unique: stockItemsGrouped.uniques,
          },
        };

        return [
          AutoOrderActions.groupStockItemsByCodeForOpenAutoOrder({ progress: updatedProgress }),
          AutoOrderActions.getMissingStockCodesAndOrderItemsOnOpenOrder({
            progress: updatedProgress,
          }),
        ];
      }),
      mergeMap((actions) => from(actions))
    )
  );
  //#endregion

  //#region [AUTO ORDER][Open Step 08][01] Get Missing Stock Codes and Order Items on Open Order
  getMissingStockCodesAndOrderItemsOnOpenOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getMissingStockCodesAndOrderItemsOnOpenOrder),
      mergeMap((action) => {
        const { progress } = action;

        return this.store.pipe(
          select(AutoOrderSelectors.selectIsOpenOrderCRUDLoading),
          filter((isLoading: boolean): boolean => isLoading === false),
          take(1),
          map(() => progress)
        );
      }),
      mergeMap((progress: IOpenAutoGeneratedOrder) => {
        const missingStockCodes = determineAutoOrderMissingStockCodes(
          progress.orderItems,
          progress.stockItems.unique
        );
        const validStockItems = determineMissingAutoOrderItemCodes(
          progress.orderItems,
          progress.stockItems.unique
        );

        const updatedProgress: IOpenAutoGeneratedOrder = {
          ...progress,
          steps: {
            ...progress.steps,
            determineMissingOrderItemCodes: true,
            determineMissingStockCodes: true,
          },
          missing: {
            ...missingStockCodes,
            ...validStockItems,
          },
        };

        const successActions: Action[] = [
          AutoOrderActions.getMissingStockCodesAndOrderItemsOnOpenOrderSuccess({
            progress: updatedProgress,
          }),
          AutoOrderActions.getEditedOrderStockItemsFromLocalStorage({
            progress: updatedProgress,
          }),
        ];
        if (updatedProgress.steps.getEditedStockItemsFromLocalStorage === false) {
          successActions.push(
            AutoOrderActions.getEditedOrderStockItemsFromLocalStorage({
              progress: updatedProgress,
            })
          );
        } else {
          successActions.push(
            AutoOrderActions.getEditedOrderStockItemsFromLocalStorageSuccess({
              progress: updatedProgress,
              orderId: updatedProgress.orderDetails.orderId,
            })
          );
        }

        return from(successActions);
      })
    )
  );

  //#endregion

  //#region [AUTO ORDER][Open Step 09][01] Get Edited Order Stock Items from Local Storage
  getEditedOrderStockItemsFromLocalStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getEditedOrderStockItemsFromLocalStorage),
      withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn))),
      filter(([_, isUserLoggedIn]) => isUserLoggedIn),
      mergeMap(([action]) =>
        this.store.pipe(
          select(AutoOrderSelectors.selectIsOpenOrderCRUDLoading),
          filter((isLoading: boolean): boolean => isLoading === false),
          take(1),
          map(() => action)
        )
      ),
      mergeMap((action) => {
        try {
          const { progress } = action;

          const localStorageKey = this.localStorageService.generateLocalStorageKey(
            LocalStorageKeysEnum.aoOpenOrder,
            progress.orderDetails.orderId
          );

          const editedItems: IAutoOrderOrderStockItem[] =
            this.localStorageService.getLocalStorageItem(localStorageKey);

          const lsOnHand: Record<string, string> = this.localStorageService.getLocalStorageItem(
            `ao-${progress.orderDetails.orderId}-on-hand`
          );
          const lsQuantity: Record<string, string> = this.localStorageService.getLocalStorageItem(
            `ao-${progress.orderDetails.orderId}-quantity`
          );
          const lsPrice: Record<string, string> = this.localStorageService.getLocalStorageItem(
            `ao-${progress.orderDetails.orderId}-price`
          );

          const updatedProgress: IOpenAutoGeneratedOrder = {
            ...progress,
            localStorageEditedItems: editedItems ?? [],
            localStorageStreams: {
              onHand: lsOnHand,
              quantity: lsQuantity,
              price: lsPrice,
            },
            steps: {
              ...progress.steps,
              getEditedStockItemsFromLocalStorage: true,
            },
          };

          return from([
            AutoOrderActions.getEditedOrderStockItemsFromLocalStorageSuccess({
              progress: updatedProgress,
              orderId: updatedProgress.orderDetails.orderId,
            }),
          ]);
        } catch (error) {
          return from([
            NotificationActions.setGlobalErrorOccurrence({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                name: 'AutoOrderOpenOrderError',
                originalError: error,
              },
            }),
          ]);
        }
      })
    )
  );

  //#endregion

  //#region [AUTO ORDER][Open Step 10] Open Auto Order Success
  openAutoOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getEditedOrderStockItemsFromLocalStorageSuccess),
      mergeMap(({ orderId }) =>
        this.store.pipe(
          select(AutoOrderSelectors.selectIsOpenOrderCRUDLoading),
          filter((isLoading: boolean): boolean => isLoading === false),
          take(1),
          map(() => AutoOrderActions.openAutoOrderSuccess({ orderId, success: true }))
        )
      )
    )
  );
  //#endregion

  //#endregion

  //#region [AUTO ORDER][Open Order][Add Items] Add Items To Open Order
  addItemsToOpenOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.addItemsToOpenOrder),
      withLatestFrom(
        this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore))
      ),
      switchMap(([action, isUserLoggedIn, store]) => {
        const { orderId, stockItems, supplierId } = action;
        if (!isUserLoggedIn || !store?.storeId || !orderId) return EMPTY;

        return from(
          this.autoOrderService.addStockItemsToOrder(store.storeId, orderId, stockItems, supplierId)
        ).pipe(
          switchMap(() =>
            defer(() =>
              from(
                this.modalController.create({
                  component: SharedModalBasicComponent,
                  componentProps: { ...AUTO_ORDER_OPEN_ORDER_FLAG_ITEMS },
                  backdropDismiss: true,
                  cssClass: ['shared-basic-modal-css-medium'],
                })
              ).pipe(
                switchMap((modal: IModalRef) => from(modal.present()).pipe(map(() => modal))),
                switchMap((modal: IModalRef) =>
                  from(modal.onDidDismiss()).pipe(
                    map((modalResponse: IModalDismissResult<ISharedModalBasicResponse>) => {
                      const actions: Action[] = [AutoOrderActions.addItemsToOpenOrderSuccess()];
                      if (modalResponse?.data?.buttonPressed === 'button-accept') {
                        const taggedItems: Record<string, string[]> = {};

                        stockItems.forEach((item: IAutoOrderOrderStockItem) => {
                          const existingTags = item._tags ?? [];

                          taggedItems[item.code] = Array.from(
                            new Set([...existingTags, 'update_supplier'])
                          );
                        });

                        actions.push(AutoOrderActions.setStockTagsForAddStockItem({ taggedItems }));
                      }
                      return actions;
                    }),
                    switchMap((actions: Action[]) => from(actions.length > 0 ? actions : EMPTY))
                  )
                )
              )
            )
          ),
          catchError((error) =>
            from([
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  name: 'AutoOrderOpenOrderError',
                  originalError: error,
                },
              }),
              AutoOrderActions.setOpenOrderLoadingIndicator({
                property: 'isOpenOrderRemoveItemsLoading',
                value: false,
              }),
            ])
          )
        );
      })
    )
  );
  //#endregion

  //#region [AUTO ORDER][Open Order][Add Stock Tags] Set Stock Item Tags For Added Open Order Items
  setStockItemTagsForAddedOpenOrderItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setStockItemTagsForAddedOpenOrderItems),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      switchMap(([action, store]) => {
        const { taggedItems } = action;
        if (Object.keys(taggedItems).length === 0) {
          return EMPTY;
        }

        return from(this.collectionSharedStockService.updateStockTags(taggedItems, store)).pipe(
          switchMap(() => of(AutoOrderActions.setStockTagsForAddStockItemSuccess())),
          catchError((error) =>
            from([
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  name: 'AutoOrderOpenOrderError',
                  originalError: error,
                },
              }),
              AutoOrderActions.setOpenOrderLoadingIndicator({
                property: 'isOpenOrderSetItemTagsLoading',
                value: false,
              }),
            ])
          )
        );
      })
    )
  );

  //#endregion

  //#region [AUTO ORDER][Open Order] Remove Items From Open Order
  removeItemsFromOpenOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.removeItemsFromOpenOrder),
      withLatestFrom(
        this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(AutoOrderSelectors.selectOpenAutoGeneratedOrderById))
      ),
      switchMap(([action, isUserLoggedIn, store, selectedOrder]) => {
        const { orderId, codesToRemove } = action;
        if (
          !isUserLoggedIn ||
          !store.storeId ||
          !selectedOrder ||
          !orderId ||
          !codesToRemove.length
        )
          return EMPTY;

        return from(
          this.autoOrderService.removeStockItemFromOrder(
            PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID(store.storeId, orderId),
            codesToRemove
          )
        ).pipe(
          map(() =>
            AutoOrderActions.removeItemsFromOpenOrderSuccess({
              orderId,
              codesToRemove,
            })
          ),
          catchError((error) =>
            from([
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  name: 'AutoOrderOpenOrderError',
                  originalError: error,
                },
              }),
              AutoOrderActions.setOpenOrderLoadingIndicator({
                property: 'isOpenOrderRemoveItemsLoading',
                value: false,
              }),
            ])
          )
        );
      })
    )
  );
  //#endregion

  //#region [AUTO ORDER][Set Property] Set Property On Auto Order

  setPropertyOnAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setPropertyOnAutoOrder),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn))
      ),
      mergeMap(([action, store, isUserLoggedIn]) => {
        if (!isUserLoggedIn) return EMPTY;
        const { orderId, value, field } = action.property;
        return from(
          this.autoOrderService.updateDocumentField<IAutoOrderOrder, IAutoOrderOrder[typeof field]>(
            PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID(store.storeId, orderId),
            field,
            value
          )
        ).pipe(
          map(() => AutoOrderActions.setPropertyOnAutoOrderSuccess()),
          catchError((error) =>
            from([
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  name: 'AutoOrderOpenOrderError',
                  originalError: error,
                },
              }),
            ])
          )
        );
      })
    )
  );

  //#endregion

  //#region [AUTO ORDER][Orders] Remove Failed Items And Retry Auto Order
  removeFailedItemsAndRetryAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.removeFailedItemsAndRetryAutoOrder),
      switchMap((action) => {
        const {storeId, orderId, codesToRemove, order} = action;
        if (!storeId || !orderId || codesToRemove.length === 0) {
          return EMPTY;
        }
        return from(this.autoOrderService.getEligibleRetryAutoOrder(storeId, orderId)).pipe(
          mergeMap(() => from(
            this.autoOrderService.removeFailedItemsFromAutoOrder(
              storeId,
              orderId,
              codesToRemove,
              order,
            ),
          )),
          mergeMap((updatedOrder: IAutoOrderOrder) => of(
            AutoOrderActions.removeItemsFromOpenOrderSuccess({orderId, codesToRemove}),
            AutoOrderActions.setRetryAutoOrder({
              order: updatedOrder,
              skipRetryableFailureCheck: true,
            }),
          )),
          catchError((error: IGlobalError) => {
            const isIneligible: boolean =
              error instanceof Error && error.message === AUTO_ORDER_RETRY_NOT_VALID;
            if (isIneligible) {
              void this.presentRetryIneligibleModal(orderId);
            }
            return of(AutoOrderActions.setRetryAutoOrderFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                name: isIneligible ? 'AutoOrderRetryIneligibleError' : 'AutoOrderRemoveFailedItemsAndRetryError',
                originalError: error,
                storeIds: [storeId],
              },
            }));
          }),
        );
      }),
    ),
  );
  //#endregion

  // ====================================================================================================
  // Send Auto Order
  // ====================================================================================================

  // '[Auto Order][Orders] Set Retry Auto Order',
  setRetryAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setRetryAutoOrder),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      mergeMap(([action, store, user]) => {
        const orderId: string = action.order.orderId;
        const storeId: string = store.storeId;
        return from(this.autoOrderService.submitEligibleAutoOrderRetry(
          storeId,
          orderId,
          action.skipRetryableFailureCheck !== true,
        )).pipe(
          map(() => AutoOrderActions.setRetryAutoOrderSuccess()),
          catchError((error: unknown) => {
            const isIneligible: boolean =
              error instanceof Error && error.message === AUTO_ORDER_RETRY_NOT_VALID;
            const failureError: IGlobalError = {
              ...(error instanceof Error ? error : new Error(String(error))),
              action: JSON.stringify(action),
              actionType: action.type,
              originalError: error,
              storeIds: [storeId],
              userId: user.id,
              name: isIneligible ? 'AutoOrderRetryIneligibleError' : (error instanceof Error ? error.name : 'Error'),
              message: isIneligible
                ? 'Order no longer exists or is not eligible for retry'
                : (error instanceof Error ? error.message : String(error)),
            };
            const failure = AutoOrderActions.setRetryAutoOrderFailure({
              error: failureError,
            });
            if (isIneligible) {
              void this.presentRetryIneligibleModal(orderId);
            }
            return of(failure);
          })
        );
      })
    )
  );

  watchPendingRetryAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setRetryAutoOrderSuccess),
      withLatestFrom(
        this.store.pipe(select(AutoOrderSelectors.selectPendingRetryAutoOrderId)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      ),
      filter(([, pendingOrderId, store]: [Action, string | null, IStore | undefined]) =>
        pendingOrderId != null && !!store?.storeId,
      ),
      switchMap(([, pendingOrderId, store]: [Action, string | null, IStore]) => {
        const orderId: string = pendingOrderId;
        const storeId: string = store.storeId;
        return this.autoOrderService.watchAutoOrderDocument(storeId, orderId).pipe(
          takeUntil(this.actions$.pipe(
            ofType(
              AutoOrderActions.setRetryAutoOrder,
              AutoOrderActions.setRetryAutoOrderFailure,
              AutoOrderActions.syncPendingRetryAutoOrderFromFirestore,
            ),
          )),
          distinctUntilChanged((previous: IAutoOrderOrder, next: IAutoOrderOrder): boolean =>
            previous.status === next.status
            && previous.iqStatus === next.iqStatus,
          ),
          filter((order: IAutoOrderOrder): boolean =>
            shouldClearPendingRetryAutoOrder(orderId, [order]),
          ),
          take(1),
          map((order: IAutoOrderOrder) => AutoOrderActions.syncPendingRetryAutoOrderFromFirestore({storeId, order})),
        );
      }),
    ),
  );

  // '[Auto Order][Email] Update Auto Order - Order Status Field',
  updateAutoOrderStatusField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.updateAutoOrderStatusField),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      mergeMap(([action, store, user]) => {
        const { orderId, status, field } = action;
        return from(
          this.collectionSharedSuppliersService.updateDocumentField<IAutoOrderOrder, string>(
            path_operational_stores_data_auto_ordering_auto_orders_order_id(store.storeId, orderId),
            field,
            status
          )
        ).pipe(
          map(() => {
            return AutoOrderActions.updateAutoOrderStatusFieldSuccess();
          }),
          catchError((error: IGlobalError) => {
            return of(
              AutoOrderActions.updateAutoOrderStatusFieldFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              })
            );
          })
        );
      })
    )
  );

  // '[AUTO ORDER][Submit Orders] Submit Auto Orders'
  submitAutoOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.submitAutoOrders),
      withLatestFrom(
        this.store.pipe(select(UserAccessSelectors.selectUser)),
        this.store.pipe(select(selectSelectedStore))
      ),
      mergeMap(([action, user, store]) => {
        const sanitizedRecipients: { [orderId: string]: string[] } = {};
        const droppedByOrderId: { [orderId: string]: string[] } = {};
        for (const orderId of action.orderIds) {
          const original: string[] = action.recipients?.[orderId] ?? [];
          const valid: string[] = filterValidEmails(original);
          const invalid: string[] = findInvalidEmails(original);
          sanitizedRecipients[orderId] = valid;
          if (invalid.length > 0) {
            droppedByOrderId[orderId] = invalid;
          }
        }
        if (Object.keys(droppedByOrderId).length > 0) {
          logConsoleInfo('submitAutoOrders$', {
            message: 'Stripped invalid email addresses before writing order.recipients',
            droppedByOrderId,
          });
        }
        return from(
          this.firebaseService.submitAutoOrder(
            action.storeId,
            action.orderIds,
            sanitizedRecipients,
            action.skipEmails
          )
        ).pipe(
          mergeMap(() => {
            void this.modalController.dismiss();
            return [
              AutoOrderActions.submitAutoOrdersSuccess({ orderIds: action.orderIds }),
              AutoOrderActions.updateStateAutoOrderOrder({ orderIds: action.orderIds, store }),
            ];
          }),
          catchError((error: IGlobalError) => {
            logConsoleError('submitAutoOrders$', error);
            return of(
              AutoOrderActions.submitAutoOrdersFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                  userId: user.id,
                },
              })
            );
          })
        );
      })
    )
  );

  // ====================================================================================================
  // Get Auto Orders
  // ====================================================================================================

  // '[Auto Order][Stock Items] Get Auto Order Stock Items with User Selected Store',
  getAutoOrderStockItemsWithUserSelectedStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderStockItemsWithUserSelectedStore),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      mergeMap(([action, store, user]) =>
        this.collectionSharedStockService.getStockItemsCast(store, action.query, 'objectID').pipe(
          mergeMap((stockItems: StockItem[]) => {
            return of(
              AutoOrderActions.getAutoOrderStockItemsWithUserSelectedStoreSuccess({
                stockItems,
                store: action.store,
                orderId: action.orderId,
                stream: action.stream,
              })
            );
          }),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.getAutoOrderStockItemsWithUserSelectedStoreFailure({
                store,
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
                orderId: action.orderId,
                stream: action.stream,
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Recipients] Set Auto Order Supplier Emails',
  setAutoOrderSupplierEmailsWithSelectedUserStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setAutoOrderSupplierEmailsWithSelectedUserStore),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      mergeMap(([action, store, user]) => {
        const { recipientEmails, supplierId, typeOfEmail } = action;
        return from(
          this.collectionOperationalAutoOrderingService.setDocument<Record<string, string[]>>(
            path_operational_stores_data_storeId_data_auto_ordering_supplier_emails_suppId(
              store.storeId,
              encodeStrict(supplierId)
            ),
            {
              [typeOfEmail]: recipientEmails.map((email: string) => email),
            }
          )
        ).pipe(
          map(() => {
            return AutoOrderActions.setAutoOrderSupplierEmailsSuccess();
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.setAutoOrderSupplierEmailsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              })
            )
          )
        );
      })
    )
  );

  // '[Auto Order][Recipients] Set Auto Order Supplier Emails',
  setAutoOrderSupplierEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setAutoOrderSupplierEmails),
      withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectUser))),
      mergeMap(([action, user]) => {
        const { recipientEmails, supplierId, store, typeOfEmail } = action;
        return from(
          this.collectionOperationalAutoOrderingService.setDocument<Record<string, string[]>>(
            path_operational_stores_data_storeId_data_auto_ordering_supplier_emails_suppId(
              store.storeId,
              encodeStrict(supplierId)
            ),
            {
              [typeOfEmail]: recipientEmails.map((email: string) => email),
            }
          )
        ).pipe(
          map(() => {
            return AutoOrderActions.setAutoOrderSupplierEmailsSuccess();
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.setAutoOrderSupplierEmailsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              })
            )
          )
        );
      })
    )
  );

  //'[Auto Order][Recipients] Delete Auto Order Supplier Emails',
  deleteAutoOrderSupplierEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.deleteAutoOrderSupplierEmails),
      withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectUser))),
      mergeMap(([action, user]) => {
        const { supplierId, store } = action;
        return from(
          this.collectionOperationalAutoOrderingService.deleteField<{ email: string[] }>(
            `${path_operational_stores_data_storeId_data_auto_ordering_supplier_emails_suppId(
              store.storeId,
              encodeStrict(supplierId)
            )}`,
            'selected'
          )
        ).pipe(
          map(() => {
            return AutoOrderActions.deleteAutoOrderSupplierEmailsSuccess();
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.deleteAutoOrderSupplierEmailsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              })
            )
          )
        );
      })
    )
  );

  // '[Auto Order][Recipients] Set Auto Order Supplier Additional Recipient Emails',
  setAutoOrderSupplierAddRecipientEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setAutoOrderSupplierAddRecipientEmails),
      withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectUser))),
      mergeMap(([action, user]) => {
        const { recipientEmails, supplierId, store } = action;
        return from(
          this.collectionSharedSuppliersService.updateDocumentField<ISupplier, string[]>(
            createSharedPaths().setStoreId(store.storeId).supplierInfo(supplierId),
            'addEmails',
            recipientEmails
          )
        ).pipe(
          map(() => {
            return AutoOrderActions.setAutoOrderSupplierAddRecipientEmailsSuccess();
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.setAutoOrderSupplierAddRecipientEmailsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              })
            )
          )
        );
      })
    )
  );

  //#region [AUTO ORDER][Supplier Emails] Get Specified Supplier Emails For Selected Store
  getSpecifiedSupplierEmailsForSelectedStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getSpecifiedSupplierEmailsForSelectedStore),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectIsUserLoggedIn))
      ),
      mergeMap(([action, store, isUserLoggedIn]) => {
        if (!isUserLoggedIn) return EMPTY;
        const { supplierIds, displayModal, order, storeId } = action;
        const currentStoreId = storeId || store?.storeId;
        return from(
          this.autoOrderService.getSupplierEmails(
            path_operational_stores_data_storeId_data_auto_ordering_supplier_emails(currentStoreId),
            supplierIds
          )
        ).pipe(
          mergeMap((emails: IAutoOrderSupplierEmails[]) => {
            if (displayModal === true) {
              return from(
                this.modalController.create({
                  component: SharedModalOrderRecipientsComponent,
                  componentProps: {
                    modalController: this.modalController,
                    setOrder: order,
                    setSupplierEmails: emails,
                    userStore: store,
                  },
                  cssClass: ['shared-basic-modal-css'],
                  backdropDismiss: true,
                })
              ).pipe(
                switchMap((modalController) =>
                  from(modalController.present()).pipe(map(() => modalController))
                ),
                switchMap((modalController) => from(modalController.onDidDismiss())),
                map(() => {
                  this.store.dispatch(clearAllAutoOrderSupplierEmails());
                  return AutoOrderActions.getSpecifiedSupplierEmailsForSelectedStoreSuccess({
                    emails,
                    storeId: store.storeId,
                  });
                })
              );
            }

            return of(
              AutoOrderActions.getSpecifiedSupplierEmailsForSelectedStoreSuccess({
                emails,
                storeId: store.storeId,
              })
            );
          }),
          catchError((error: IGlobalError) =>
            from([
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  name: 'AutoOrderPreparedOrderError',
                  originalError: error,
                  storeIds: [store.storeId],
                },
              }),
              AutoOrderActions.setSupplierEmailsLoading({ isLoading: false }),
            ])
          )
        );
      })
    )
  );
  //#endregion

  // '[Auto Order][Orders] Get Auto Order - Orders'
  getAutoOrderOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderOrders),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([action, store]) =>
        this.collectionOperationalAutoOrderingService
          .getAutoOrdersFirebaseQueries(store, action.firebaseQueries)
          .pipe(
            map((autoOrderOrders: IAutoOrderOrder[]) =>
              AutoOrderActions.getAutoOrderOrdersSuccess({
                autoOrderOrders,
                store,
                firebaseQueries: action.firebaseQueries,
                statusChip: action.statusChip,
              })
            ),
            catchError((error: IGlobalError) => {
              console.error(error);
              return of(
                AutoOrderActions.getAutoOrderOrdersFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store.storeId],
                  },
                })
              );
            })
          )
      )
    )
  );

  // '[Auto Order][Stock Items] Get Auto Order Stock Items'
  getAutoOrderStockItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderStockItems),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      switchMap(([{ query, orderId, stream }, store]) => {
        return [
          AutoOrderActions.getAutoOrderStockItemsWithUserSelectedStore({
            query,
            store,
            orderId,
            stream,
          }),
        ];
      })
    )
  );

  //  '[Auto Order][Deletion Rules] Get Auto Order Stock Item Deletion Rules'
  getAutoOrderOrderStockItemDeletionRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderStockItemDeletionRules),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([action, store]) =>
        from(
          this.collectionOperationalSingularDocumentsService.getAutoOrderingStockItemDeletionRules(
            store
          )
        ).pipe(
          mergeMap((disabledRules: IDisableStockItemRule) => {
            return of(
              AutoOrderActions.getAutoOrderStockItemDeletionRulesSuccess({ store, disabledRules })
            );
          }),
          catchError((error: IGlobalError) => {
            console.error(`Error in getAutoOrderOrderStockItemDeletionRules:`, error);
            return of(
              AutoOrderActions.getAutoOrderStockItemDeletionRulesFailure({
                store,
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Orders] Update Auto Order - Order Email Status',
  updateAutoOrderOrderEmailStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.updateAutoOrderOrderEmailStatus),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      mergeMap(([action, store, user]) => {
        const { orderId, status } = action;
        return from(
          this.collectionSharedSuppliersService.updateDocumentField<IAutoOrderOrder, string>(
            path_operational_stores_data_auto_ordering_auto_orders_order_id(store.storeId, orderId),
            'userId',
            user.id
          )
        ).pipe(
          mergeMap(() =>
            from(
              this.collectionSharedSuppliersService.updateDocumentField<IAutoOrderOrder, string>(
                path_operational_stores_data_auto_ordering_auto_orders_order_id(
                  store.storeId,
                  orderId
                ),
                'emailStatus',
                status
              )
            )
          ),
          map(() => {
            return AutoOrderActions.setAutoOrderSupplierAddRecipientEmailsSuccess();
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.setAutoOrderSupplierAddRecipientEmailsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                },
              })
            )
          )
        );
      })
    )
  );

  // '[Auto Order][Orders] Update Auto Order - Order Email Status',
  updateAutoOrderOrderEmailRecipients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.updateAutoOrderOrderEmailRecipients),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([action, store]) => {
        const { orderId, recipients } = action;
        const sanitizedRecipients: string[] = filterValidEmails(recipients);
        const droppedRecipients: string[] = findInvalidEmails(recipients);
        if (droppedRecipients.length > 0) {
          logConsoleInfo('updateAutoOrderOrderEmailRecipients$', {
            message: 'Stripped invalid email addresses before writing order.recipients',
            orderId,
            droppedRecipients,
          });
        }
        return from(
          this.collectionSharedSuppliersService.updateDocumentField<IAutoOrderOrder, string[]>(
            path_operational_stores_data_auto_ordering_auto_orders_order_id(store.storeId, orderId),
            'recipients',
            sanitizedRecipients
          )
        ).pipe(
          map(() => {
            return AutoOrderActions.updateAutoOrderOrderEmailRecipientsSuccess();
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.updateAutoOrderOrderEmailRecipientsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                },
              })
            )
          )
        );
      })
    )
  );

  // '[Auto Order][Email] Send Auto Order Emails',
  sendAutoOrderEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.sendAutoOrderEmails),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      mergeMap(([action, store, user]) => {
        const { order } = action;
        return from(
          this.collectionAdminCloudService.setDocument<ICloudEmailer>(
            path_admin_cloud_emailer_email_orders(order.orderId),
            {
              orderId: order.orderId,
              storeId: store.storeId,
              userId: order.userId,
            }
          )
        ).pipe(
          map(() => {
            return AutoOrderActions.sendAutoOrderEmailsSuccess();
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.sendAutoOrderEmailsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              })
            )
          )
        );
      })
    )
  );

  setAutoOrderingOrderFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setAutoOrderingOrderFilter),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
        this.store.pipe(select(selectOpenOrderSettings))
      ),
      map(([{ filter }, store, user, openOrderSettings]) =>
        StoreSettingsActions.updateOpenOrderSettingsSuccess({
          openOrderSettings: {
            ...openOrderSettings,
            filters: {
              purchaseOrders: filter.purchaseOrderUnset
                ? 'unset'
                : filter.purchaseOrderEqualZero
                ? 'equal_to_zero'
                : 'greater_than_zero',
              suggestionGreaterThanZero: filter.suggestionPositive,
              orderQuantityGreaterThanZero: filter.quantityPositive,
            },
          },
          store,
        })
      )
    )
  );

  // '[Auto Order][Order Settings] Get Auto Order - Order Settings'
  getAutoOrderOrderSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderOrderSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      mergeMap(([_, store, user]) => {
        return from(
          this.collectionUsersSettingsService.getDocument<IAutoOrderOrderingSettings>(
            path_users_userId_settings_stores_storeId_orders_settings(user.id, store.storeId)
          )
        ).pipe(
          map((settings: IAutoOrderOrderingSettings) => {
            return AutoOrderActions.getAutoOrderOrderSettingsSuccess({
              settings,
              storeId: store.storeId,
            });
          }),
          catchError((error: IGlobalError) =>
            of(AutoOrderActions.getAutoOrderOrderSettingsFailure({ error }))
          )
        );
      })
    )
  );

  // '[Auto Order][Stock Items] Get Auto Order Stock Items',
  deleteAutoOrderOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.deleteAutoOrderOrder),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      switchMap(([{ orderIds }, store]) => {
        return [AutoOrderActions.deleteAutoOrderOrderWithStoreId({ orderIds, store })];
      })
    )
  );

  // '[Auto Order][Order] Update Auto Order Item At Property On Order',
  updateAutoOrderItemAtPropertyOnOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.updateItemPropertyOnAutoOrder),
      withLatestFrom(
        this.store.pipe(select(AutoOrderSelectors.selectSelectedAutoGeneratedOrderIds))
      ),
      switchMap(([{ stockCode, property, newValue, updateReducer }, orderIds]) => {
        return orderIds.length > 0
          ? [
              AutoOrderActions.updateItemPropertyOnAutoOrderSuccess({
                stockCode,
                property,
                newValue,
                selectedOrderId: orderIds[0],
                updateReducer,
              }),
            ]
          : [];
      })
    )
  );

  //   '[Auto Order][Stock Items] Get Auto Order Stock Items with User Selected Store',
  deleteAutoOrderOrderWithStoreId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.deleteAutoOrderOrderWithStoreId),
      mergeMap((action) =>
        this.collectionAdminFunctionsService
          .addDocumentToCollection<{
            orderIds: string[];
            storeId: string;
          }>(path_admin_functions_orders_instructions_delete_order(), {
            orderIds: action.orderIds,
            storeId: action.store.storeId,
          })
          .pipe(
            map(() =>
              AutoOrderActions.deleteAutoOrderOrderSuccess({
                store: action.store,
                orderIds: action.orderIds,
              })
            ),
            catchError((error: IGlobalError) => {
              console.error(error);
              return of(
                AutoOrderActions.deleteAutoOrderOrderFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [action.store.storeId],
                  },
                })
              );
            })
          )
      )
    )
  );

  //'[Add Item] Get Typesense Stock Items'

  getTypesenseFacetCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getTypesenseFacetCounts),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([action, store]) =>
        from(
          this.typeSenseService.searchStockItems('', 1, 50, store, {
            code: { value: true, name: 'Code' },
            desc: { value: true, name: 'Description' },
          })
        ).pipe(
          mergeMap((stockItemsRaw: TypeSenseSearchResponse<StockItem>) => {
            const facetCounts: IFacetCounts = createFacetCountsMap(stockItemsRaw.facet_counts);
            return [
              AutoOrderActions.getTypesenseFacetCountsSuccess({
                facetCounts,
              }),
            ];
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.getTypesenseFacetCountsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                },
              })
            )
          )
        )
      )
    )
  );

  getPaginationResultsForAddStockItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getPaginationResultsForAddStockItem),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(AutoOrderSelectors.selectAddItemLineColours)),
        this.store.pipe(select(AutoOrderSelectors.selectAddStockItemTypesenseData))
      ),
      mergeMap(
        ([
          action,
          store,
          lineColours,
          {
            paginationData: { searchedValue },
          },
        ]) =>
          from(
            this.typeSenseService.searchStockItems(searchedValue, action.pageNumber, 50, store, {
              code: { value: true, name: 'Code' },
              desc: { value: true, name: 'Description' },
            })
          ).pipe(
            mergeMap((stockItemsRaw: TypeSenseSearchResponse<StockItem>) => {
              const paginationData: IPaginationData = {
                totalItems: stockItemsRaw.found,
                pageSize: 50,
                currentPage: action.pageNumber,
                searchedValue: searchedValue,
              };
              const mappedStockItems = mapTypeSenseObjectToGeneric<StockItem>(stockItemsRaw);
              const stockItems = mappedStockItems.map((item: StockItem) => ({
                ...item,
                storeId: store.storeId,
                store: store.name,
                lineColourValue: lineColours?.[item.lineColourCode]
                  ? lineColours[item.lineColourCode].value
                  : '',
              }));
              return [
                AutoOrderActions.getTypesenseStockItemsSuccess({
                  stockItems,
                  paginationData,
                }),
              ];
            }),
            catchError((error: IGlobalError) =>
              of(
                AutoOrderActions.getTypesenseStockItemsFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store.storeId],
                  },
                })
              )
            )
          )
      )
    )
  );

  // '[Auto Order][Auto Delete] Set Auto Order Auto Delete Settings',
  setAutoOrderAutoDeleteSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setAutoOrderAutoDeleteSettings),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([action, store]) =>
        from(
          this.collectionUsersSettingsService.setDocument(
            path_stores_storeId_settings_ao_order_expiration(store.storeId),
            action.settings
          )
        ).pipe(
          map(() => AutoOrderActions.setAutoOrderAutoDeleteSettingsSuccess()),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.setAutoOrderAutoDeleteSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Auto Delete] Get Auto Order Auto Delete Settings'
  getAutoOrderAutoDeleteSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderAutoDeleteSettings),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([action, store]) =>
        from(
          this.collectionUsersSettingsService.getDocument(
            path_stores_storeId_settings_ao_order_expiration(store.storeId)
          )
        ).pipe(
          map((settings: IAutoOrderExpirationSettings) =>
            AutoOrderActions.getAutoOrderAutoDeleteSettingsSuccess({ settings })
          ),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.getAutoOrderAutoDeleteSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  getAddItemLineColours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAddItemLineColours),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([action, selectedStore]) =>
        from(
          this.firebaseService.getStoreDataDoc(action.pageStoreDocument, selectedStore.storeId)
        ).pipe(
          mergeMap((lineColours: TLineColour) => {
            return [AutoOrderActions.getAddItemLineColoursSuccess({ lineColours })];
          }),
          catchError((error: IGlobalError) => {
            return of(
              AutoOrderActions.getAddItemLineColoursFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [selectedStore.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  setStockTagsForAddStockItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setStockTagsForAddStockItem),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      switchMap(([{ taggedItems }, store]) => {
        if (Object.keys(taggedItems).length > 0) {
          return from(this.collectionSharedStockService.updateStockTags(taggedItems, store)).pipe(
            switchMap(() => of(AutoOrderActions.setStockTagsForAddStockItemSuccess())),
            catchError((error: IGlobalError) =>
              of(
                AutoOrderActions.setStockTagsForAddStockItemFailure({
                  error: {
                    ...error,
                    originalError: error,
                    storeIds: [store.storeId],
                  },
                })
              )
            )
          );
        } else {
          return EMPTY;
        }
      })
    )
  );

  setCustomOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setCustomOrder),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser))
      ),
      switchMap(([{ supplierData, stockItemData }, store, user]) => {
        const autoOrderFromScratch = {
          emailStatus: 'READY',
          errorMsg: null,
          generated: new Date(),
          iqStatus: 'READY',
          orderId: '',
          orderSettings: {
            growth: 0,
            monthsHistory: 0,
            period: 0,
            stockDays: 0,
          },
          owned: user.id,
          pdfStatus: 'READY',
          scheduled: 'CREATED',
          status: 'READY',
          supplierId: supplierData[0].supplierId,
          userId: user.id,
        };
        const basePath = path_operational_stores_data_auto_ordering_auto_orders_GET(store.storeId);
        return from(
          this.collectionOperationalAutoOrderingService.addDocument(basePath, autoOrderFromScratch)
        ).pipe(
          switchMap((docId: string) => {
            const docPath = path_operational_stores_data_auto_ordering_auto_orders_order_id(
              store.storeId,
              docId
            );
            return from(
              this.collectionOperationalAutoOrderingService.setDocument(docPath, { orderId: docId })
            ).pipe(
              switchMap(() =>
                from(
                  this.firebaseService.addItemsToAutoOrder(
                    store.storeId,
                    docId,
                    stockItemData,
                    supplierData[0].supplierId as string
                  )
                ).pipe(
                  map(() =>
                    AutoOrderActions.setCustomOrderSuccess({ supplierData, stockItemData })
                  ),
                  catchError((error: IGlobalError) =>
                    of(
                      AutoOrderActions.setCustomOrderFailure({
                        error: {
                          ...error,
                          originalError: error,
                          storeIds: [store.storeId],
                          userId: user.id,
                        },
                      })
                    )
                  )
                )
              )
            );
          }),
          catchError((error: IGlobalError) =>
            of(
              AutoOrderActions.setCustomOrderFailure({
                error: {
                  ...error,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              })
            )
          )
        );
      })
    )
  );

  // ========================================================================
  // NEW EFFECTS - Component Firebase Calls Migration
  // ========================================================================

  // '[Auto Order][Order Grid] Prepare Auto Order For Submission'
  prepareAutoOrderForSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.prepareAutoOrderForSubmission),
      withLatestFrom(this.store.pipe(select(SharedSelectors.selectCurrentVersion))),
      mergeMap(([action, frontendVersion]) => {
        const writeLog: IAutoOrderQuantityWriteLog = buildAutoOrderQuantityWriteLog({
          actionSource: action.actionSource,
          environment: environment.environment,
          frontendVersion,
          orderId: action.order.orderId,
          preparedItemsCount: Object.keys(action.preparedItems ?? {}).length,
          status: action.status,
          storeId: action.storeId,
          supplierId: action.order.supplierId,
          supplierName: action.order.supplierName,
          total: action.total,
          writeResult: AutoOrderQuantityWriteResultEnum.STARTED,
        });
        logConsoleInfo('setAOQuantities', writeLog);
        return from(
          this.firebaseService.setAOQuantities(
            action.storeId,
            adaptToLegacyAutoOrder(action.order),
            action.preparedItems,
            action.total,
            action.status,
            action.comment,
            action.dirtyCodes
          )
        ).pipe(
          map(() => {
            logConsoleSuccess('setAOQuantities', {
              ...writeLog,
              writeResult: AutoOrderQuantityWriteResultEnum.SUCCESS,
            });
            return AutoOrderActions.prepareAutoOrderForSubmissionSuccess({
              storeId: action.storeId,
              orderId: action.order.orderId,
              priceEditsSnapshot: action.priceEditsSnapshot,
              quantitiesSnapshot: action.quantitiesSnapshot,
            });
          }),
          catchError((error: IGlobalError) => {
            const failureLog: IAutoOrderQuantityWriteLog = {
              ...writeLog,
              errorMessage: getAutoOrderQuantityWriteErrorMessage(error),
              writeResult: AutoOrderQuantityWriteResultEnum.FAILURE,
            };
            logConsoleError('setAOQuantities', failureLog);
            return of(
              AutoOrderActions.prepareAutoOrderForSubmissionFailure({
                orderId: action.order.orderId,
                error: {
                  ...error,
                  action: JSON.stringify(failureLog),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        );
      })
    )
  );

  // '[Auto Order][Order Grid] Prepare Auto Order For Submission Success'
  // Clears the browser draft once the prepare write has been persisted. This lives
  // here rather than in the order grid so the draft is still cleared when the user
  // closes the order while the write is in flight. The draft is left alone when it
  // no longer matches what was written, which means it holds newer edits.
  clearOrderDraftAfterPrepare$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AutoOrderActions.prepareAutoOrderForSubmissionSuccess),
        tap(({orderId, priceEditsSnapshot, quantitiesSnapshot}): void => {
          if (!orderId) {
            return;
          }

          const storedQuantities: Record<string, number> | null =
            this.localStorageService.getLocalStorageItem(localStorageKeyOrderQuantity(orderId));
          const storedPrices: Record<string, number> | null =
            this.localStorageService.getLocalStorageItem(localStorageKeyOrderPrice(orderId));

          const hasNewerEdits: boolean = !areQuantityDraftsEqual(storedQuantities, quantitiesSnapshot)
            || !areQuantityDraftsEqual(storedPrices, priceEditsSnapshot);

          if (hasNewerEdits) {
            return;
          }

          localStorage.removeItem(localStorageKeyOrderQuantity(orderId));
          localStorage.removeItem(localStorageKeyOrderPrice(orderId));
          localStorage.removeItem(localStorageKeyOrderOnHand(orderId));
        }),
      ),
    {dispatch: false},
  );

  // '[Auto Order][Order Grid] Update Stock Item On Hold'
  updateStockItemOnHold$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.updateStockItemOnHold),
      mergeMap((action) =>
        from(this.firebaseService.stockUpdate(action.storeId, action.updates)).pipe(
          map(() => AutoOrderActions.updateStockItemOnHoldSuccess()),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.updateStockItemOnHoldFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Order Grid] Set Stock Item Disabled From Order'
  setStockItemDisabledFromOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setStockItemDisabledFromOrder),
      withLatestFrom(
        this.store.pipe(select(AutoOrderSelectors.selectAutoOrderStockDeletionRules)),
        this.store.pipe(select(selectItemEnablingSettings)),
        this.store.pipe(select(AutoOrderSelectors.selectStockItemDisableBaselines)),
      ),
      concatMap(([action, disableRules, enableRules, baselines]) => {
        const { enable, item, storeId } = action;
        const loadingMessage: string = enable
          ? `Enabling ${item.code} for stock…`
          : `Disabling ${item.code} for stock…`;
        const updates: Record<string, StockItemUpdateObj> | null = buildDisableStockItemUpdate(
          item,
          disableRules?.[storeId],
          enableRules?.rulesDoc,
          enable,
          baselines[item.code],
        );

        return from(presentSharedOverlayLoadingModal(this.modalController, loadingMessage)).pipe(
          switchMap((loadingModal: IModalRef) =>
            from(this.writeStockItemDisabledStatus(storeId, item, updates, enable)).pipe(
              map(() => AutoOrderActions.setStockItemDisabledFromOrderSuccess({
                code: item.code,
                enable,
                fieldUpdates: updates?.[item.code]?.n,
                previousValues: updates?.[item.code]?.o,
              })),
              catchError((error: IGlobalError) => {
                const globalError: IGlobalError = {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [storeId],
                };

                return from([
                  AutoOrderActions.setStockItemDisabledFromOrderFailure({ error: globalError }),
                  NotificationActions.setGlobalErrorOccurrence({ error: globalError }),
                ]);
              }),
              finalize((): void => {
                void loadingModal.dismiss();
              }),
            ),
          ),
        );
      }),
    ),
  );

  // '[Auto Order][Order Grid] Update Stock Item Flag'
  updateStockItemFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.updateStockItemFlag),
      mergeMap((action) =>
        from(
          this.firebaseService.updateStockTags(
            action.storeId,
            [action.stockId],
            'ao_flag',
            action.flagged
          )
        ).pipe(
          map(() => AutoOrderActions.updateStockItemFlagSuccess()),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.updateStockItemFlagFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Edit Schedule] Get Auto Ordering Schedule Settings'
  getAutoOrderingScheduleSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderingScheduleSettings),
      switchMap((action) =>
        from(this.firebaseService.getAutoOrderingScheduleSubs(action.storeId)).pipe(
          mergeMap((dayObservables) => {
            // dayObservables is Record<day, Observable<Record<supplierId, AOScheduleElement>>>
            const dayKeys = Object.keys(dayObservables);

            // Create an array of observables that emit updateScheduleDayData actions
            const dayActions$ = dayKeys.map((day) =>
              dayObservables[day].pipe(
                map((scheduleData) =>
                  AutoOrderActions.updateScheduleDayData({
                    storeId: action.storeId,
                    day,
                    scheduleData,
                  })
                )
              )
            );

            // Merge all day observables and prepend with success action
            return merge(
              of(
                AutoOrderActions.getAutoOrderingScheduleSettingsSuccess({
                  storeId: action.storeId,
                  dayCount: dayKeys.length,
                })
              ),
              ...dayActions$
            ).pipe(
              takeUntil(
                this.actions$.pipe(
                  ofType(AutoOrderActions.cleanupAutoOrderingScheduleSettings),
                  filter((cleanupAction) => cleanupAction.storeId === action.storeId)
                )
              )
            );
          }),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.getAutoOrderingScheduleSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Edit Schedule] Update Auto Ordering Schedule Settings'
  updateAutoOrderingScheduleSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.updateAutoOrderingScheduleSettings),
      mergeMap((action) =>
        from(this.firebaseService.updateAutoOrderingSchedules(action.storeId, action.updates)).pipe(
          map(() => AutoOrderActions.updateAutoOrderingScheduleSettingsSuccess()),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.updateAutoOrderingScheduleSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Unscheduled] Get Auto Ordering Schedule For Day'
  getAutoOrderingScheduleForDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getAutoOrderingScheduleForDay),
      mergeMap((action) =>
        from(this.firebaseService.getAutoOrderingScheduleDay(action.storeId, action.day)).pipe(
          map((scheduleDay) =>
            AutoOrderActions.getAutoOrderingScheduleForDaySuccess({
              storeId: action.storeId,
              day: action.day,
              scheduleDay,
            })
          ),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.getAutoOrderingScheduleForDayFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // '[Auto Order][Unscheduled] Request Unscheduled Auto Orders'
  requestUnscheduledAutoOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.requestUnscheduledAutoOrders),
      mergeMap((action) =>
        from(
          this.firebaseService.requestAutoOrderUnscheduled(action.storeId, action.orderData)
        ).pipe(
          map(() => AutoOrderActions.requestUnscheduledAutoOrdersSuccess()),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.requestUnscheduledAutoOrdersFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  // ====================================================================================================
  // Schedule Loading Orchestration Effects
  // ====================================================================================================

  loadScheduleForStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.loadScheduleForStore),
      map(({ storeId }) => AutoOrderActions.getAutoOrderingScheduleSettings({ storeId }))
    )
  );

  trackScheduleLoadingProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AutoOrderActions.updateScheduleDayData,
        AutoOrderActions.getAutoOrderingScheduleSettingsSuccess,
        // Re-check loading progress when suppliers arrive. Without this the
        // schedule can stay loading forever for stores that have auto-order
        // access but no schedule documents: all 7 days emit `{}` synchronously
        // via `startWith` before suppliers have loaded, and there is no later
        // schedule emission to retrigger this effect.
        SharedActions.getStoreSuppliersByStoreSuccess
      ),
      withLatestFrom(
        this.store.pipe(select(AutoOrderSelectors.selectAutoOrderingState)),
        this.store.pipe(select(SharedSelectors.selectSuppliersByUserSelectedStore)),
        this.store.pipe(select(selectSelectedStore))
      ),
      switchMap(([action, autoOrderState, suppliers, selectedStore]) => {
        // Schedule actions carry `storeId` directly; the supplier success action
        // carries `store` instead. Fall back to the currently selected store so
        // supplier-load triggers retarget the in-flight schedule load.
        let storeId: string | undefined;
        if ('storeId' in action && action.storeId) {
          storeId = action.storeId;
        } else if ('store' in action && action.store?.storeId) {
          storeId = action.store.storeId;
        } else {
          storeId = selectedStore?.storeId;
        }

        if (!storeId) {
          return EMPTY;
        }

        const loadingState = autoOrderState.scheduleLoadingState[storeId];
        if (!loadingState) {
          return EMPTY;
        }

        const scheduleData = autoOrderState.autoOrderScheduleData[storeId] || {};
        const daysLoaded = Object.keys(scheduleData);

        const suppliersLoaded = Object.keys(suppliers).length > 0;

        let expectedDayCount = loadingState.expectedDayCount;
        if (
          action.type === AutoOrderActions.getAutoOrderingScheduleSettingsSuccess.type &&
          'dayCount' in action
        ) {
          expectedDayCount = action.dayCount;
        }

        const progressAction = AutoOrderActions.setScheduleLoadingProgress({
          storeId,
          daysLoaded,
          suppliersLoaded,
          expectedDayCount,
        });

        const allDataLoaded =
          expectedDayCount > 0 && daysLoaded.length === expectedDayCount && suppliersLoaded;

        if (allDataLoaded) {
          return of(progressAction, AutoOrderActions.scheduleDataLoadingComplete({ storeId }));
        }

        return of(progressAction);
      })
    )
  );

  scheduleLoadingTimeout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AutoOrderActions.loadScheduleForStore),
        switchMap(({ storeId }) =>
          of(
            AutoOrderActions.scheduleDataLoadingFailed({
              storeId,
              error: {
                name: 'ScheduleLoadTimeout',
                message: 'Schedule data took too long to load',
                originalError: new Error('Timeout after 30 seconds'),
                storeIds: [storeId],
              },
            })
          ).pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(
                  AutoOrderActions.scheduleDataLoadingComplete,
                  AutoOrderActions.cleanupAutoOrderingScheduleSettings
                ),
                filter((action) => action.storeId === storeId)
              )
            )
          )
        )
      ),
    { dispatch: false }
  );

  // '[Auto Order][Order Settings] Set Auto Order Order Settings'
  setAutoOrderOrderSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.setAutoOrderOrderSettings),
      mergeMap((action) =>
        from(
          this.collectionUsersSettingsService.setDocument(
            path_users_userId_settings_stores_storeId_orders_settings(
              action.userId,
              action.storeId
            ),
            action.settings
          )
        ).pipe(
          map(() => AutoOrderActions.setAutoOrderOrderSettingsSuccess()),
          catchError((error: IGlobalError) => {
            console.error(error);
            return of(
              AutoOrderActions.setAutoOrderOrderSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.storeId],
                },
              })
            );
          })
        )
      )
    )
  );

  /**
   * Create a stock take from the open-order uncounted list and add the selected codes as pending lines.
   */
  createStockTakeFromUncountedOrderItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.createStockTakeFromUncountedOrderItems),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      switchMap(([{name, userAssigned, binLocation, includeDelCol, codes}, store]) => {
        const storeId = store?.storeId;
        if (!storeId || codes.length === 0) {
          return of(AutoOrderActions.createStockTakeFromUncountedOrderItemsFailure({
            error: {
              name: 'StockTakeCreateError',
              message: 'Select at least one item and a store before creating a stock take.',
            } as IGlobalError,
          }));
        }
        return from(this.writeUncountedOrderStockTake(storeId, name, userAssigned, binLocation, includeDelCol, codes)).pipe(
          switchMap((stockTakeId: string) =>
            from(this.showOrderStockTakeCreatedModal(name, codes.length)).pipe(
              map(() => AutoOrderActions.createStockTakeFromUncountedOrderItemsSuccess({stockTakeId})),
              catchError((modalError: unknown) => {
                logConsoleError('[AUTO ORDER] Stock take created but success modal failed', modalError);
                return of(AutoOrderActions.createStockTakeFromUncountedOrderItemsSuccess({stockTakeId}));
              }),
            ),
          ),
          catchError((error: Error) =>
            from(this.showOrderStockTakeFailedModal(error.message)).pipe(
              map(() => AutoOrderActions.createStockTakeFromUncountedOrderItemsFailure({
                error: {
                  name: 'StockTakeCreateError',
                  message: error.message || 'Failed to create the stock take.',
                } as IGlobalError,
              })),
              catchError((modalError: unknown) => {
                logConsoleError('[AUTO ORDER] Stock take create failed and error modal failed', modalError);
                return of(AutoOrderActions.createStockTakeFromUncountedOrderItemsFailure({
                  error: {
                    name: 'StockTakeCreateError',
                    message: error.message || 'Failed to create the stock take.',
                  } as IGlobalError,
                }));
              }),
            ),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private autoOrderService: AutoOrderService,
    private collectionAdminCloudService: CollectionAdminCloudService,
    private collectionAdminFunctionsService: CollectionAdminFunctionsService,
    private collectionOperationalAutoOrderingService: CollectionOperationalAutoOrderingService,
    private collectionOperationalSingularDocumentsService: CollectionOperationalSingularDocumentsService,
    private collectionSharedStockService: CollectionSharedStockService,
    private collectionSharedSuppliersService: CollectionSharedSuppliersService,
    private collectionUsersSettingsService: CollectionUsersSettingsService,
    private typeSenseService: TypeSenseService,
    private firebaseService: FirebaseService,
    private modalController: ModalControllerService,
    private localStorageService: LocalStorageService,
    private readonly stockTakeFirebaseService: StockTakeFirebaseService,
    private readonly store: Store
  ) {}

  /**
   * Writes the disabled tag and, when the store has disabling rules, pushes the stock update
   * for a single order item immediately. The two writes cannot be batched together, so a failed
   * stock update rolls the tag back to keep the item's tag and its rule-driven fields in step.
   */
  private async writeStockItemDisabledStatus(
    storeId: string,
    item: StockItem,
    updates: Record<string, StockItemUpdateObj> | null,
    enable: boolean,
  ): Promise<void> {
    await this.firebaseService.updateStockTags(storeId, [item.code], 'disabled', enable);
    if (!updates) {
      return;
    }

    try {
      await this.firebaseService.stockUpdate(storeId, updates);
    } catch (error) {
      try {
        await this.firebaseService.updateStockTags(storeId, [item.code], 'disabled', !enable);
      } catch (rollbackError) {
        throw {
          ...(error as object),
          message: `${item.code} is now tagged as ${enable ? 'enabled' : 'disabled'} but its stock fields were not updated, `
            + 'and the tag could not be rolled back. Retry the change or contact support.',
          name: 'AutoOrderDisableItemPartialWriteError',
          originalError: error,
          rollbackError,
        };
      }
      throw error;
    }
  }

  private async presentRetryIneligibleModal(orderId: string): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'OK',
      modalTitle: 'Order Retry Failed',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'This order cannot be retried',
      contextDescription: [
        `Order ${orderId} no longer exists or is not eligible for retry.`,
        'No submission was queued and no order record was created.',
      ],
    };
    const modalControl = await this.modalController.create({
      backdropDismiss: false,
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css'],
    });
    await modalControl.present();
  }

  /**
   * Standardized error handler for effects
   * Creates a properly formatted global error object with action context
   *
   * @param error - The error that occurred
   * @param action - The action that triggered the effect
   * @param context - Additional context (e.g., error name, storeIds, userId)
   * @returns An observable of the formatted error action
   *
   * @example
   * catchError((error) => this.handleEffectError(error, action, {
   *   errorName: 'AutoOrderLoadError',
   *   storeIds: [store.storeId]
   * }))
   */
  private handleEffectError(
    error: IGlobalError | Error | unknown,
    action: Action,
    context: {
      errorName: string;
      storeIds?: string[];
      userId?: string;
      failureAction?: Action;
      loadingActions?: Action[];
    }
  ): Observable<Action> {
    // Safely handle error spreading by checking if it's an object
    const errorBase = typeof error === 'object' && error !== null ? error : {};

    // Extract message from error if available
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
        ? error.message
        : 'An unexpected error occurred';

    const formattedError: IGlobalError = {
      ...errorBase,
      message,
      action: JSON.stringify(action),
      actionType: action.type,
      name: context.errorName,
      originalError: error,
      ...(context.storeIds ? { storeIds: context.storeIds } : {}),
      ...(context.userId ? { userId: context.userId } : {}),
    };

    const actions: Action[] = [
      NotificationActions.setGlobalErrorOccurrence({ error: formattedError }),
    ];

    // Add any loading state reset actions
    if (context.loadingActions) {
      actions.push(...context.loadingActions);
    }

    // Add the specific failure action if provided
    if (context.failureAction) {
      actions.push(context.failureAction);
    }

    return from(actions);
  }

  private async writeUncountedOrderStockTake(
    storeId: string,
    name: string,
    userAssigned: string,
    binLocation: string,
    includeDelCol: boolean,
    codes: string[],
  ): Promise<string> {
    const stockTakeId = await this.stockTakeFirebaseService.createStockTake(storeId, {
      name,
      userAssigned,
      binLocation,
      includeDelCol,
    });
    const batchLimit = 450;
    try {
      for (let index = 0; index < codes.length; index += batchLimit) {
        await this.stockTakeFirebaseService.batchWriteStockTakeItems(
          storeId,
          stockTakeId,
          codes.slice(index, index + batchLimit).map((code: string) => ({
            code,
            qtyCounted: 0,
            previousQtyCounted: 0,
            timesCounted: 0,
            status: StockTakeItemStatus.pending,
          })),
        );
      }
    } catch (error) {
      try {
        await this.stockTakeFirebaseService.deleteStockTake(storeId, stockTakeId);
      } catch {
        // The item write already failed. Surface that error after the cleanup attempt.
      }
      throw error;
    }
    return stockTakeId;
  }

  private async showOrderStockTakeCreatedModal(name: string, itemCount: number): Promise<void> {
    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      cssClass: ['shared-basic-modal-css-small'],
      componentProps: {
        modalTitleIcon: IconsEnum.checkCircle,
        modalTitleIconColor: IconColourEnum.green,
        modalTitle: 'Stock take created',
        contextDescription: [
          `"${name}" was created with ${itemCount} selected item${itemCount === 1 ? '' : 's'}.`,
          'Ordering is unchanged.',
        ],
        buttonAccept: false,
        buttonAcceptText: '',
        buttonClose: true,
        buttonCloseText: 'OK',
        buttonCloseCross: false,
      } as ISharedModalBasic,
    });
    await modal.present();
    await modal.onDidDismiss();
  }

  private async showOrderStockTakeFailedModal(message: string): Promise<void> {
    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      cssClass: ['shared-basic-modal-css-small'],
      componentProps: {
        modalTitleIcon: IconsEnum.alertBox,
        modalTitleIconColor: IconColourEnum.red,
        modalTitle: 'Stock take not created',
        contextDescription: [message || 'Failed to create the stock take.'],
        buttonAccept: false,
        buttonAcceptText: '',
        buttonClose: true,
        buttonCloseText: 'Close',
        buttonCloseCross: false,
      } as ISharedModalBasic,
    });
    await modal.present();
    await modal.onDidDismiss();
  }
}
